import {
    api,
    apiErrorMessage,
    listSpec,
    useAuthStore,
    useIsPlatformHost,
    useZodForm,
    type Prefetch,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Avatar,
    Banner,
    Button,
    Checkbox,
    CloseIcon,
    ColorTag,
    ConfirmDialog,
    DateTime,
    EditIcon,
    Field,
    FormSheet,
    Input,
    LockIcon,
    PageHeader,
    RetryIcon,
    RevokeIcon,
    RowActions,
    Section,
    SendIcon,
    StatusBadge,
    type ListColumnDef,
} from '@bemor/web-kit/ui';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import type { Role, StaffInvitation, StaffMember } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { useList, useWrite } from '@/lib/queries';
import {
    type ApprovalRequested,
    type RevokedCount,
} from '@bemor/web-kit/staff-auth';

const schema = z.object({
    email: z.email('An email'),
    roles: z.array(z.string()).min(1, 'At least one role'),
    tenant_scope: z.enum(['platform', 'tenant']),
});

/** The staff statuses (design `BO_STAFF`). */
const signIn = (m: StaffMember) =>
    `${m.user.sso ? 'Google' : 'Password'}${m.totp_enabled_at ? ' + code' : ''}`;

function RolePicker({
    roles,
    value,
    onChange,
}: {
    roles: Role[];
    value: string[];
    onChange: (v: string[]) => void;
}) {
    return (
        <ul className="grid grid-cols-1 gap-1 md:grid-cols-2">
            {roles.map((r) => (
                <li key={r.key}>
                    <label className="flex items-center gap-2 text-sm">
                        <Checkbox
                            checked={value.includes(r.key)}
                            onCheckedChange={(v) =>
                                onChange(
                                    v === true
                                        ? [...value, r.key]
                                        : value.filter((k) => k !== r.key),
                                )
                            }
                        />
                        <ColorTag color={r.color ?? null}>{r.name}</ColorTag>
                    </label>
                </li>
            ))}
        </ul>
    );
}

type Notice = {
    tone: 'success' | 'info' | 'danger';
    title: string;
    body?: ReactNode;
    action?: ReactNode;
};

/**
 * `/staff` (design `backoffice/staff`; the row actions are missing from the design, ROUND-7-DRAFT section 6): who
 * works in Admin. Per member: roles, disable, reset the authenticator (four eyes: a second staff manager grants
 * it in Approvals), end every session. Per open invitation: resend (a fresh link; the old one stops) and revoke.
 */
const PATH = '/staff/staff';
const PAGE_KEY = 'staff';

/** The page's other reads: the open invitations and the roles to pick from (BM-46 §2.0). */
export const staffReads = () => ({
    invitations: listSpec('/staff/invitations'),
    roles: listSpec('/staff/roles'),
});

const membersPrefetch = adminListPrefetch(PAGE_KEY, PATH);

/** The route's prefetch: the views, then the members of the view; the invitations and the roles. */
export const staffPrefetch: Prefetch = (context) => [
    ...membersPrefetch(context),
    ...Object.values(staffReads()),
];

const staffFilters = filterDefs(PATH, {
    q: 'Search',
    status: 'Status',
    tenant_scope: 'Scope',
    role: 'Role',
    range: 'Added',
});

export function StaffPage() {
    // BM-102: the host decides the scope of an invitation (platform host: platform staff; tenant host: tenant staff)
    const platformHost = useIsPlatformHost();
    const session = useAuthStore((s) => s.session);
    const reads = staffReads();
    const invitations = useList<StaffInvitation>(reads.invitations.path);
    const roles = useList<Role>(reads.roles.path);
    const roleByKey = new Map(
        (roles.data?.data.items ?? []).map((r) => [r.key, r] as const),
    );
    const staffRoles = (roles.data?.data.items ?? []).filter(
        (r) => r.audience === 'staff',
    );
    const [inviting, setInviting] = useState(false);
    const [editing, setEditing] = useState<StaffMember | null>(null);
    const [editRoles, setEditRoles] = useState<string[]>([]);
    const [revoking, setRevoking] = useState<StaffInvitation | null>(null);
    const [resetting, setResetting] = useState<StaffMember | null>(null);
    const [ending, setEnding] = useState<StaffMember | null>(null);
    const [notice, setNotice] = useState<Notice | null>(null);
    const form = useZodForm(schema, {
        defaultValues: {
            email: '',
            roles: [],
            tenant_scope: platformHost ? 'platform' : 'tenant',
        },
    });
    const invite = useWrite(
        async (values: z.infer<typeof schema>) =>
            (await api().post<StaffInvitation>('/staff/invitations', values))
                .data,
        ['/staff/invitations'],
    );
    const update = useWrite(
        async (body: { member: StaffMember; patch: Record<string, unknown> }) =>
            (
                await api().patch<StaffMember>(
                    `/staff/staff/${body.member.id}`,
                    { ...body.patch, version: body.member.version },
                )
            ).data,
        ['/staff/staff'],
    );
    const revoke = useWrite(
        async (i: StaffInvitation) =>
            api().delete(`/staff/invitations/${i.id}`),
        ['/staff/invitations'],
    );
    const resend = useWrite(
        async (i: StaffInvitation) =>
            (
                await api().post<StaffInvitation>(
                    `/staff/invitations/${i.id}/resend`,
                )
            ).data,
        ['/staff/invitations'],
    );
    const resetTotp = useWrite(
        async (body: { member: StaffMember; reason: string }) =>
            (
                await api().post<ApprovalRequested>(
                    `/staff/staff/${body.member.id}/totp-reset`,
                    { reason: body.reason },
                )
            ).data,
        ['/staff/approvals'],
    );
    const endSessions = useWrite(
        async (m: StaffMember) =>
            (await api().delete<RevokedCount>(`/staff/staff/${m.id}/sessions`))
                .data,
        ['/staff/staff'],
    );
    const fail = (title: string) => (e: unknown) =>
        setNotice({ tone: 'danger', title, body: apiErrorMessage(e) });

    const columns: ListColumnDef<StaffMember>[] = [
        {
            key: 'name',
            aliases: ['who'],
            label: 'Name',
            sortKey: 'name',
            boardCard: true,
            cell: (m) => (
                <span className="flex min-w-0 items-center gap-2">
                    <Avatar name={m.user.name} size="sm" />
                    <span className="flex min-w-0 flex-col">
                        <span className="truncate">{m.user.name}</span>
                        <span className="text-fg-2 truncate text-xs">
                            {m.user.email}
                        </span>
                    </span>
                </span>
            ),
            locked: 'start',
        },
        {
            key: 'roles',
            label: 'Roles',
            csv: (m) => m.roles.join(', '),
            // BM-46 §2.5: each role in its staff-chosen color
            cell: (m) => (
                <span className="flex flex-wrap gap-1">
                    {m.roles.map((key) => {
                        const role = roleByKey.get(key);
                        return (
                            <ColorTag key={key} color={role?.color ?? null}>
                                {role?.name ?? key}
                            </ColorTag>
                        );
                    })}
                </span>
            ),
        },
        {
            key: 'tenant_scope',
            aliases: ['scope'],
            label: 'Scope',
            sortKey: 'tenant_scope',
            cell: (m) =>
                m.tenant_scope === 'platform'
                    ? 'Platform'
                    : m.merchant_scope.length > 0
                      ? `Tenant, ${m.merchant_scope.length} merchants`
                      : 'Tenant',
        },
        {
            key: 'status',
            label: 'Status',
            sortKey: 'status',
            cell: (m) => <StatusBadge status={m.status} domain="member" />,
            groupable: { key: 'status', statusDomain: 'member' },
        },
        {
            key: 'sign_in',
            label: 'Sign-in',
            cell: (m) => <span className="text-fg-2">{signIn(m)}</span>,
        },
        {
            key: 'last_login_at',
            aliases: ['last'],
            label: 'Last sign-in',
            kind: 'datetime',
            sortKey: 'last_login_at',
            cell: (m) => <DateTime value={m.user.last_login_at} empty="—" />,
            noTruncate: true,
        },
        {
            key: 'actions',
            label: 'Actions',
            locked: 'end',
            actions: true,
            // BM-84 (rule G.6, design 0.7.0 staff): one menu, re-based on the kit's RowActions
            cell: (m) => {
                const self = m.user.id === session?.actor.id;
                return (
                    <RowActions
                        name={m.user.name}
                        data-testid="staff-actions"
                        items={[
                            {
                                label: 'Edit roles',
                                icon: EditIcon,
                                onSelect: () => {
                                    setEditing(m);
                                    setEditRoles(m.roles);
                                },
                            },
                            {
                                label:
                                    m.status === 'active'
                                        ? 'Disable'
                                        : 'Enable',
                                icon: LockIcon,
                                hidden: self,
                                onSelect: () =>
                                    update.mutate(
                                        {
                                            member: m,
                                            patch: {
                                                status:
                                                    m.status === 'active'
                                                        ? 'disabled'
                                                        : 'active',
                                            },
                                        },
                                        { onError: fail('Not saved') },
                                    ),
                            },
                            {
                                label: 'End sessions',
                                icon: CloseIcon,
                                hidden: self,
                                onSelect: () => setEnding(m),
                            },
                            {
                                label: 'Reset authenticator',
                                icon: RetryIcon,
                                hidden: self,
                                destructive: true,
                                onSelect: () => setResetting(m),
                            },
                        ]}
                    />
                );
            },
        },
    ];
    const { state, query: members } = useAdminList<StaffMember>(
        PAGE_KEY,
        PATH,
        { columns, filters: staffFilters },
    );
    const open = invitations.data?.data.items ?? [];
    return (
        <div className="flex flex-col gap-6">
            <PageHeader
                title="Staff"
                meta={<AsOfBadge asOf={members.freshness.asOf} />}
                primaryAction={
                    <Button variant="brand" onClick={() => setInviting(true)}>
                        Invite
                    </Button>
                }
            />
            {notice ? (
                <Banner
                    tone={notice.tone}
                    title={notice.title}
                    action={notice.action}
                    data-testid="staff-notice"
                >
                    {notice.body}
                </Banner>
            ) : null}
            <AdminListPage
                noun="staff members"
                state={state}
                query={members}
                rowKey={(m) => m.id}
                controls={{
                    q: { kind: 'search' },
                    status: { domain: 'member' },
                    role: {
                        options: staffRoles.map((r) => ({
                            value: r.key,
                            label: r.name,
                        })),
                    },
                }}
            />
            <Section
                title="Open invitations"
                description="A resent invitation has a new link and a new expiry; the old link stops working."
                list
                testId="staff-invitations"
            >
                <ul className="m-0 list-none p-0">
                    {open.map((i) => (
                        <li
                            key={i.id}
                            data-testid="invitation-row"
                            className="border-border-1 flex flex-wrap items-center justify-between gap-3 px-5 py-3 not-first:border-t"
                        >
                            <span className="flex min-w-0 flex-col">
                                <span className="truncate text-sm">
                                    {i.email}
                                </span>
                                <span className="text-fg-2 text-xs">
                                    {i.tenant_scope === 'platform'
                                        ? 'Platform'
                                        : 'Tenant'}
                                    {', expires '}
                                    <DateTime
                                        value={i.expires_at}
                                        format="date"
                                        empty="—"
                                    />
                                </span>
                            </span>
                            {/* BM-84 (rule G.6, design 0.7.0 staff): the pending invitation's actions in one menu */}
                            <RowActions
                                name={i.email}
                                items={[
                                    {
                                        label: 'Resend',
                                        icon: SendIcon,
                                        disabled:
                                            resend.isPending &&
                                            resend.variables?.id === i.id,
                                        onSelect: () =>
                                            resend.mutate(i, {
                                                onSuccess: () =>
                                                    setNotice({
                                                        tone: 'success',
                                                        title: 'Invitation sent again',
                                                        body: `${i.email} has a new link. The old one no longer works.`,
                                                    }),
                                                onError: fail(
                                                    'The invitation was not sent',
                                                ),
                                            }),
                                    },
                                    {
                                        label: 'Revoke',
                                        icon: RevokeIcon,
                                        destructive: true,
                                        onSelect: () => setRevoking(i),
                                    },
                                ]}
                            />
                        </li>
                    ))}
                    {invitations.data && open.length === 0 ? (
                        <li className="text-fg-2 px-5 py-3 text-sm">
                            No open invitations.
                        </li>
                    ) : null}
                </ul>
            </Section>
            {/* BM-84 (rule E.9, design 0.7.0 staff): invite and edit roles in sheets */}
            <FormSheet
                open={inviting}
                onOpenChange={setInviting}
                size="sm"
                title="Invite staff"
                submitLabel="Send invitation"
                busy={invite.isPending}
                dirty={form.formState.isDirty}
                data-testid="invite-form"
                onSubmit={form.handleSubmit((values) => {
                    setNotice(null);
                    invite.mutate(values, {
                        onSuccess: () => {
                            setInviting(false);
                            form.reset();
                        },
                        onError: fail('The invitation was not sent'),
                    });
                })}
            >
                <Field
                    id="email"
                    label="Email"
                    error={form.formState.errors.email?.message}
                >
                    <Input
                        id="email"
                        type="email"
                        {...form.register('email')}
                    />
                </Field>
                <Field
                    id="roles"
                    label="Roles"
                    error={form.formState.errors.roles?.message}
                >
                    <RolePicker
                        roles={staffRoles}
                        value={form.watch('roles')}
                        onChange={(v) =>
                            form.setValue('roles', v, {
                                shouldValidate: true,
                            })
                        }
                    />
                </Field>
                {/* BM-102: the host decides the scope. The platform host invites platform staff, a tenant host its own staff. */}
                <Field
                    id="tenant_scope"
                    label="Scope"
                    hint={
                        platformHost
                            ? 'Platform staff see every tenant. Tenant staff are invited on the tenant’s own Admin.'
                            : 'Tenant staff see this tenant only. Platform staff are invited on the platform Admin.'
                    }
                >
                    <Input
                        id="tenant_scope"
                        value={platformHost ? 'Platform' : 'Tenant'}
                        readOnly
                    />
                </Field>
            </FormSheet>
            <FormSheet
                open={editing !== null}
                onOpenChange={(o) => (o ? null : setEditing(null))}
                size="sm"
                title={`Roles of ${editing?.user.name ?? ''}`}
                submitLabel="Save roles"
                busy={update.isPending}
                submitDisabled={editRoles.length === 0}
                dirty={
                    editing !== null &&
                    [...editRoles].sort().join() !==
                        [...editing.roles].sort().join()
                }
                data-testid="edit-roles-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    if (!editing) return;
                    update.mutate(
                        { member: editing, patch: { roles: editRoles } },
                        {
                            onSuccess: () => setEditing(null),
                            onError: fail('Not saved'),
                        },
                    );
                }}
            >
                <RolePicker
                    roles={staffRoles}
                    value={editRoles}
                    onChange={setEditRoles}
                />
            </FormSheet>
            <ConfirmDialog
                open={revoking !== null}
                onCancel={() => setRevoking(null)}
                title="Revoke this invitation?"
                body={`The link sent to ${revoking?.email ?? ''} stops working.`}
                confirmLabel="Revoke invitation"
                destructive
                loading={revoke.isPending}
                onConfirm={() =>
                    revoking &&
                    revoke.mutate(revoking, {
                        onSuccess: () => setRevoking(null),
                        onError: fail('The invitation was not revoked'),
                    })
                }
            />
            <ConfirmDialog
                open={resetting !== null}
                onCancel={() => {
                    resetTotp.reset();
                    setResetting(null);
                }}
                title={`Reset the authenticator of ${resetting?.user.name ?? ''}?`}
                body="Their authenticator and recovery codes are cleared and every session ends; the next sign-in sets up a new authenticator."
                note={{
                    title: 'Needs a second staff manager',
                    body: 'Nothing changes until a different staff manager grants the request in Approvals.',
                }}
                reason={{
                    label: 'Reason',
                    required: true,
                    hint: 'The approver and the audit log see it.',
                }}
                confirmLabel="Request the reset"
                destructive
                loading={resetTotp.isPending}
                error={
                    resetTotp.error
                        ? apiErrorMessage(resetTotp.error)
                        : undefined
                }
                onConfirm={({ reason }) =>
                    resetting &&
                    resetTotp.mutate(
                        { member: resetting, reason },
                        {
                            onSuccess: () => {
                                setNotice({
                                    tone: 'info',
                                    title: 'Approval requested',
                                    body: `A second staff manager must approve the reset of ${resetting.user.name}'s authenticator in Approvals. Until then it keeps working.`,
                                    action: (
                                        <Button
                                            asChild
                                            size="sm"
                                            variant="outline"
                                            className="no-underline"
                                        >
                                            <Link to="/approvals?action=identity.staff.manage">
                                                Open Approvals
                                            </Link>
                                        </Button>
                                    ),
                                });
                                setResetting(null);
                            },
                        },
                    )
                }
            />
            <ConfirmDialog
                open={ending !== null}
                onCancel={() => {
                    endSessions.reset();
                    setEnding(null);
                }}
                title={`End every session of ${ending?.user.name ?? ''}?`}
                body="They are signed out everywhere at once and sign in again with their password or Google and the code."
                confirmLabel="End sessions"
                destructive
                loading={endSessions.isPending}
                error={
                    endSessions.error
                        ? apiErrorMessage(endSessions.error)
                        : undefined
                }
                onConfirm={() =>
                    ending &&
                    endSessions.mutate(ending, {
                        onSuccess: (r) => {
                            setNotice({
                                tone: 'success',
                                title: 'Sessions ended',
                                body: `${ending.user.name} was signed out of ${r?.revoked ?? 0} ${r?.revoked === 1 ? 'session' : 'sessions'}.`,
                            });
                            setEnding(null);
                        },
                    })
                }
            />
        </div>
    );
}
